import React, {
  CSSProperties,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { Button } from "semantic-ui-react";

import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { useLocation } from "react-router-dom";

import { useDebouncedCallback } from "react-lib/utils/hooksUtils";

type MapPinLocationProps = {
  debounceMs?: number;
  editAddress?: {
    address: EditAddressType;
  };
  height?: string;
  myLocationStyle?: CSSProperties;
  readOnly?: boolean;
  zoomControl?: boolean;
  onPinLocation: PinLocationHandler;
};

type EditAddressType = {
  latitude: string;
  longitude: string;
  name?: string;
  place_id?: string;
};

export type PinLocationHandler = (data: {
  lat: number;
  lng: number;
  place_id?: string;
  place_name?: string;
}) => any;

export type MapPinLocationRef = {
  getLocation: (targetAddress: EditAddressType) => void;
  onGetMyLocation: () => void;
};

type LocationStateType = {
  latlng: string;
  place_id: string;
  place_name?: string;
};

const myLocationStyle = {
  alignItems: "center",
  backgroundColor: "white",
  border: "2px solid #dadce0",
  borderRadius: "500rem",
  bottom: "20px",
  display: "flex",
  height: "40px",
  justifyContent: "center",
  padding: 0,
  position: "absolute",
  right: "10px",
  width: "40px",
  zIndex: 1000,
};

const API_KEY = "AIzaSyCoByISlmXq8lpdpMIJH2n-g_28hWgyDCY";

const MapPinLocation = forwardRef<MapPinLocationRef, MapPinLocationProps>((props, ref) => {
  const location = useLocation<LocationStateType>();

  const [pinLocation, setPinLocation] = useState<google.maps.LatLngLiteral>({
    lat: 0,
    lng: 0,
  });
  const [center, setCenter] = useState<Partial<google.maps.LatLngLiteral>>({});

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: API_KEY,
  });

  const mapRef = useRef<google.maps.Map | null>(null);
  const centerRef = useRef<any>({ lat: 0, lng: 0 });
  const isMounted = useRef(false);
  const initialLocationSet = useRef(false);
  const locationStateRef = useRef<LocationStateType | null>(null);

  const debouncedOnPinLocation = useDebouncedCallback(props.onPinLocation, props.debounceMs ?? 300);

  useImperativeHandle(ref, () => ({
    getLocation,
    onGetMyLocation: handleMyLocationClick,
  }));

  locationStateRef.current = location.state;

  const containerStyle = useMemo(
    () => ({
      height: props.height || "calc(100vh - 23rem)",
      width: "100%",
    }),
    [props.height]
  );

  const handleOnLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const showPosition = useCallback(
    async (data: any) => {
      const lat = data.coords.latitude;
      const lng = data.coords.longitude;
      const placeId = data.place_id;
      const placeName = data.place_name;

      centerRef.current = { lat, lng };
      setCenter({ lat, lng });
      setPinLocation({ lat, lng });

      props.onPinLocation({
        lat,
        lng,
        place_id: placeId,
        place_name: placeName,
      });
      initialLocationSet.current = true;

      const interval = setInterval(() => {
        if (!mapRef.current?.getBounds()) {
          return;
        }

        clearInterval(interval);
        isMounted.current = true;
      }, 100);
    },
    [props.onPinLocation]
  );

  const getLocation = useCallback(
    async (targetAddress?: EditAddressType) => {
      const latlng = locationStateRef.current?.latlng;
      const placeId = locationStateRef.current?.place_id;
      const placeName = locationStateRef.current?.place_name;
      const address = targetAddress ?? props.editAddress?.address;

      if (latlng) {
        const [lat, lng] = latlng.split(",");

        void showPosition({
          coords: {
            latitude: Number(lat),
            longitude: Number(lng),
          },
          place_id: placeId,
          place_name: placeName,
        });
      } else if (address?.latitude && address.longitude) {
        const lat = address.latitude;
        const lng = address.longitude;

        void showPosition({
          coords: {
            latitude: Number(lat),
            longitude: Number(lng),
          },
          place_id: address.place_id,
          place_name: address.name,
        });
      } else if (navigator.geolocation && !initialLocationSet.current && !props.readOnly) {
        navigator.geolocation.getCurrentPosition(showPosition);
      } else if (!initialLocationSet.current && !props.readOnly) {
        alert("Geolocation is not supported by this browser.");
      }
    },
    [props.editAddress, props.readOnly, showPosition]
  );

  const handlePinLocationChange = useCallback(() => {
    // ถ้าเป็น readOnly ไม่ต้องทำอะไร
    if (props.readOnly) {
      return;
    }

    if (mapRef.current && isMounted.current) {
      const updatedCenter = mapRef.current.getCenter();

      if (updatedCenter !== undefined) {
        const lat = updatedCenter.lat();
        const lng = updatedCenter.lng();

        centerRef.current = { lat, lng };
        const updatedPinLocation = { lat, lng };

        setPinLocation(updatedPinLocation);
        debouncedOnPinLocation([updatedPinLocation]);
      }
    }
  }, [debouncedOnPinLocation, props.readOnly]);

  useEffect(() => {
    // รอ history replace
    setTimeout(() => {
      void getLocation();
    }, 100);
  }, []);

  const mapOptions = useMemo(
    () => ({
      disableDefaultUI: true,
      disableDoubleClickZoom: props.readOnly, // ปิดการ double click zoom ถ้าเป็น readOnly
      draggable: !props.readOnly, // ปิดการลากแผนที่ถ้าเป็น readOnly
      fullscreenControl: false,
      gestureHandling: props.readOnly ? "none" : "greedy", // ปิดการควบคุมทั้งหมดถ้าเป็น readOnly
      mapTypeControl: false,
      panControl: false,
      rotateControl: false,
      scaleControl: false,
      scrollwheel: !props.readOnly, // ปิดการ zoom ด้วย scroll ถ้าเป็น readOnly
      streetViewControl: false,
      zoomControl: props.readOnly ? false : props.zoomControl ?? false,
    }),
    [props.readOnly, props.zoomControl]
  );

  const centerOptions = useMemo(
    () => (center.lat && center.lng ? { lat: center.lat, lng: center.lng } : undefined),
    [center]
  );

  const handleMyLocationClick = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          centerRef.current = { lat, lng };
          setCenter({ lat, lng });
          setPinLocation({ lat, lng });
          props.onPinLocation({ lat, lng });

          if (mapRef.current) {
            mapRef.current.panTo({ lat, lng });
          }
        },
        (error) => {
          console.error("Geolocation error:", error);
          alert("Unable to get your current location");
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  }, [props.onPinLocation]);

  return (
    <>
      {isLoaded && (
        <div style={{ position: "relative" }}>
          <GoogleMap
            center={centerOptions}
            mapContainerStyle={containerStyle}
            zoom={18}
            options={mapOptions}
            onCenterChanged={props.readOnly ? undefined : handlePinLocationChange} // ปิด event ถ้าเป็น readOnly
            onLoad={handleOnLoad}
            onZoomChanged={props.readOnly ? undefined : handlePinLocationChange} // ปิด event ถ้าเป็น readOnly
          >
            {!!pinLocation.lat && !!pinLocation.lng && <Marker position={pinLocation} />}
          </GoogleMap>

          {/* ซ่อนปุ่ม My Location ถ้าเป็น readOnly */}
          {!props.readOnly && (
            <Button
              style={{ ...myLocationStyle, ...props.myLocationStyle }}
              onClick={handleMyLocationClick}
            >
              <img
                style={{ width: "20px" }}
                alt="my-location.icon"
                src="/images/common/icon_my_location.png"
              />
            </Button>
          )}
        </div>
      )}
    </>
  );
});

MapPinLocation.displayName = "MapPinLocation";

export default React.memo(MapPinLocation);
